import { User } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { animations, pixelCornersCss } from "../helpers/cssImports";
import { LoginRegisterBaseClass } from "./LoginRegisterBaseClass";

/** CSS affecting the {@link LoginComponent} */
const style: string = css`
:host {
    font-family: "ka";
    width: 50%;
    display: flex;
    flex-direction: column;

}

.title {
    background-color: rgba(0, 0, 0, 0.9);
    text-align: center;
    margin: 1%;
}

.login {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10%;
    background-color: rgba(0, 0, 0);
    text-align: center;
    margin: 1%;
    flex-grow: 1;
}

.username, .password {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2%;
    font-family: "pixel";
    font-size: 40px;
    width: 100%;
}

.username input, .password input {
    padding: 1%;
    width: 100%;
    border-color: rgb(15, 122, 237);
    font-size: 35px;
    font-family: "pixel";
    border-width: 8px;
}

form {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
}

label {
    margin-right: auto;
}

.message {
    font-family: "pixel";
    font-size: 30px;
    min-height: 40px;
    color: red;
}

button[type="submit"] {
    width: 100%;
    font-family: "pixel";
    font-size: 30px;
    margin: 10%;
    background-color: rgb(15, 122, 237);
    color: white;
    padding: 2%;
    transition: background-color 0.3s ease-in-out;
}

button[type="submit"]:hover {
    background-color: black;
    animation: hover 0.5s ease-in-out 2 forwards, glow 0.5s infinite;
    cursor: pointer;
}

.sign-up {
    font-family: "pixel";
    font-size: 30px;
    text-decoration: underline;
    transition: color 0.3s ease-in-out;
}

.sign-up:hover {
    color: rgb(15, 122, 237);
    animation: hover 0.5s ease-in-out 2 forwards;
    cursor: pointer;
}

p {
    font-family: "pixel";
    font-size: 30px;
}

img {
    width: 50%;
    margin-top: -5%;
}

${pixelCornersCss}
${animations}
`;

/**
 * Represents the login webcomponent page
 * Handles the login functionality
 */
export class LoginComponent extends LoginRegisterBaseClass {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /**
     * Constructor of the web component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        this.render();
    }

    /**
     * Renders the login page UI.
     * Includes login form, styling and setting up buttons
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const loginForm: HTMLElement = this.createLoginForm();
        const elements: HTMLElement[] = htmlArray`
            <style>
                ${style}
            </style>
                ${this.createLoginTitle()}
                ${loginForm}
        `;

        this.shadowRoot.append(...elements);
        this.setUpLoginBtn(loginForm);
        this.setUpSignUp(loginForm);
        localStorage.setItem("userId", "0");
        document.body.style.backgroundImage = "url('/backgrounds/login.gif')";
    }

    /**
     * Creates title for the login page
     * @returns - HTML element of the title seciton
     */
    private createLoginTitle(): HTMLElement {
        const element: HTMLElement = html`
            <section class="title pixel-corners">
                <div>
                    <h2>Login</h2>
                </div>
            </section>
        `;

        return element;
    }

    /**
     * Creates the login form
     * Includes input for username, password and submit button
     * @returns  - The created login form HTML
     */
    private createLoginForm(): HTMLElement {
        const element: HTMLElement = html`
            <section class="login pixel-corners">
                <img class="pixel-corners--wrapper" src="/assets/img/rooms/startscreen.png" />
                <p>Login and start playing!</p>
                <form>
                    <div class="username">
                        <label for="username">> Username</label>
                        <input type="text" id="username" name="username" class="pixel-corners">
                    </div>
                    <div class="password">
                        <label for="password">> Password</label>
                        <input type="password" id="password" name="password" class="pixel-corners">
                    </div>
                    <button class="pixel-corners submit" type="submit">Login</button>
                </form>
                <div class="message"></div>
                <div class="sign-up">Register</div>
            </section>
        `;

        return element;
    }

    /**
     * Sets up the login button event listener when clicked.
     * @param element - The element to set up
     */
    private setUpLoginBtn(element: HTMLElement): void {
        const loginBtn: HTMLElement = element.querySelector(".submit") as HTMLElement;
        loginBtn.addEventListener("click", (event: Event) => this.handleLoginBtnClick(element, event));
    }

    /**
     * Handles the login button click event
     * @param element - The login form
     * @param event - Event
     */
    private async handleLoginBtnClick(element: HTMLElement, event: Event): Promise<void> {
        event.preventDefault();

        // Get the related elements
        const { username, password, message } = this.getElements(element);

        try {
            // Validate the user input
            if (!this.isValidInput(username, password, message)) return;

            // Creates an user and send it to the backend
            const user: User = this.createUser(username, password);
            const retrievedUser: User[] = await this._gameRouteService.getUser(user);

            // Handles the login after the backend validates
            this.handleLogin(retrievedUser);
        }
        catch {
            this.showMessage(message, "Incorrect username or password.");
        }
    }

    /**
     * Retrieve elemets of the login form
     * @param element - The login form
     * @returns - username, password values and the message element
     */
    private getElements(element: HTMLElement): {
        username: string;
        password: string;
        message: HTMLElement;
    } {
        return {
            username: (element.querySelector("#username") as HTMLInputElement).value,
            password: (element.querySelector("#password") as HTMLInputElement).value,
            message: element.querySelector(".message") as HTMLElement,
        };
    }

    /**
     * Handles a succesful login
     * Stores the playersessionID and userID in the local storage,  and switches to the canvas Page
     * @param retrievedUser - Array of retrieved users
     */
    private handleLogin(retrievedUser: User[]): void {
        // Update the local storage with the logged in user
        localStorage.setItem("playerSessionId", retrievedUser[0].session_id ?? "");
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("userId", String(retrievedUser[0].id ?? ""));

        // Sends the user to the canvas page
        this.handleSwitchPage(Page.Canvas);
    }

    /**
     * validates input from the user
     * @param username - Username input
     * @param password - The password input
     * @param message - The message element to display the validation msg
     * @returns - True if input is valid, false otherwise
     */
    private isValidInput(username: string, password: string, message: HTMLElement): boolean {
        if (!username || !password) {
            this.showMessage(message, "Please enter valid information.");
            return false;
        }
        return true;
    }

    /**
     * Sets up the sign up button to navigate the user to the sign up page
     * @param element - The sign up button
     */
    private setUpSignUp(element: HTMLElement): void {
        const signUp: HTMLElement = element.querySelector(".sign-up") as HTMLElement;
        signUp.addEventListener("click", () => this.handleSwitchPage(Page.Register));
    }
}
